import { useEffect, useState } from 'react'
import { Copy, QrCode, Share2 } from 'lucide-react'
import { Button, Card, Eyebrow, InfoNote } from '@/components/ui'
import { Modal } from '@/components/Modal'
import { useToast } from '@/components/Toast'

/**
 * Lagets egen beställningslänk.
 *
 * Lagledaren delar den i den kanal föräldrarna redan använder – lagchatten,
 * gruppen, ett mejl hen ändå skulle skicka. Vi mejlar den medvetet inte
 * själva: varje lag har redan en fungerande väg till sina föräldrar, och att
 * lägga ett utskick i vår app tillför ingenting men lägger till ett ställe
 * där leveransen kan fallera.
 *
 * QR-koden är för träningen. Lagledaren håller upp telefonen efter passet och
 * föräldrarna skannar – ingen behöver skriva av en adress i regnet.
 */
export function InviteCard({ teamId, teamName }: { teamId: string; teamName: string }) {
  const toast = useToast()
  const [showQr, setShowQr] = useState(false)

  const url = `${window.location.origin}/bestall/lag/${teamId}`

  function copy() {
    void navigator.clipboard?.writeText(url).then(
      () => toast.success('Länken är kopierad.'),
      () => toast.error('Kunde inte kopiera. Markera länken och kopiera för hand.'),
    )
  }

  async function share() {
    // Systemets egen delning når lagchatten direkt. Saknas den – på en dator
    // – faller vi tillbaka på att kopiera, vilket är det man ändå gör där.
    if (!navigator.share) return copy()
    try {
      await navigator.share({
        title: `Beställ Lirahäften – ${teamName}`,
        text: `Här beställer du Lirahäften till ${teamName}.`,
        url,
      })
    } catch {
      // Avbruten delning är inget fel.
    }
  }

  return (
    <Card className="space-y-3 p-5">
      <Eyebrow>Länk till föräldrarna</Eyebrow>
      <p className="text-sm text-muted">
        Dela den här länken i lagets kanal. Föräldern beställer sina häften, betalar, och får
        koderna när du bockat av betalningen.
      </p>

      <p className="rounded-card bg-surface-2 px-3.5 py-2.5 font-mono text-xs break-all">{url}</p>

      <div className="flex flex-wrap gap-2">
        <Button icon={<Share2 size={16} />} onClick={() => void share()}>
          Dela länken
        </Button>
        <Button variant="quiet" icon={<Copy size={16} />} onClick={copy}>
          Kopiera
        </Button>
        <Button variant="quiet" icon={<QrCode size={16} />} onClick={() => setShowQr(true)}>
          Visa QR
        </Button>
      </div>

      {showQr && <QrModal url={url} teamName={teamName} onClose={() => setShowQr(false)} />}
    </Card>
  )
}

function QrModal({
  url,
  teamName,
  onClose,
}: {
  url: string
  teamName: string
  onClose: () => void
}) {
  const [dataUrl, setDataUrl] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void import('qrcode')
      .then((module) =>
        module.default.toDataURL(url, {
          width: 320,
          margin: 1,
          color: { dark: '#0E15A8FF', light: '#FFFFFFFF' },
        }),
      )
      .then((result) => {
        if (!cancelled) setDataUrl(result)
      })
    return () => {
      cancelled = true
    }
  }, [url])

  return (
    <Modal
      open
      onClose={onClose}
      title={`Beställningslänk – ${teamName}`}
      size="sm"
      footer={
        <Button fullWidth onClick={onClose}>
          Stäng
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="flex justify-center">
          <div className="rounded-card bg-white p-4">
            {dataUrl ? (
              <img src={dataUrl} alt={`QR-kod till beställningssidan för ${teamName}`} width={320} height={320} />
            ) : (
              <div className="size-[320px]" />
            )}
          </div>
        </div>
        <InfoNote>
          Håll upp telefonen efter träningen så skannar föräldrarna direkt. Koden leder till
          beställningssidan med {teamName} redan valt.
        </InfoNote>
      </div>
    </Modal>
  )
}
